/**
 * Nova's window: the chosen layout, the Settings window, notices, and the
 * keyboard shortcuts every layout shares.
 *
 * The layout is a registry entry (Compact, Pro, Radio, or a plugin's). In a
 * window too narrow for the chosen one, Compact is shown until there is room
 * again, without changing the choice. A layout a plugin adds (Radio) appears
 * once the plugins have loaded; until then the window waits briefly instead of
 * flashing Pro, and if the plugin is off, Pro is shown.
 */

import { FfmpegNotice } from "./shell/FfmpegNotice";
import { useEffect, useState } from "react";
import { checkOnStart } from "./core/updates";
import { AppProvider, useApp } from "./core/app";
import { registerBuiltins } from "./core/builtins";
import { addFolder, newPlaylist, rescanLibrary } from "./core/commands";
import { player } from "./core/player";
import { startPlayLog } from "./core/playLog";
import { pluginsSettled } from "./core/plugins";
import { bus } from "./core/bus";
import { useRegistry } from "./core/registry";
import { focusSearch } from "./shell/menus";
import { EditDialogs } from "./shell/EditDialogs";
import { InsertDialog } from "./shell/InsertDialog";
import { FlowSession } from "./flow/useFlowEditor";
import { SettingsDialog } from "./shell/SettingsDialog";
import { SetupDialog } from "./shell/SetupDialog";
import { Toasts } from "./shell/Toast";
import { UpdatePrompt } from "./shell/UpdatePrompt";

registerBuiltins();
startPlayLog();

const COMPACT_BELOW = 620;
const SAVES = new Set(["flow", "studio"]);

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.innerWidth < COMPACT_BELOW);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < COMPACT_BELOW);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
}

function useShortcuts() {
  const app = useApp();
  const { layouts } = useRegistry();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el?.closest("input, textarea, select, [contenteditable='true']");
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const run = (fn: () => unknown) => {
        e.preventDefault();
        e.stopPropagation();
        void fn();
      };
      if (mod && (key === "k" || key === "f")) return run(focusSearch);
      if (mod && key === ",") return run(() => app.openSettings("general"));
      if (mod && key === "o") return run(() => addFolder(app));
      if (mod && key === "n") return run(() => newPlaylist(app));
      if (mod && key === "b") return run(() => app.setSidebar(!app.sidebar));
      if (mod && key === "j") return run(() => app.setInspector(!app.inspector));
      if (mod && key === "m") return run(() => player.toggleMute());
      if (mod && key === "i" && app.focused) return run(() => app.navigate("studio", { id: app.focused! }));
      if (mod && /^[1-9]$/.test(e.key) && !app.layoutLocked && layouts[Number(e.key) - 1]) return run(() => app.setLayout(layouts[Number(e.key) - 1].id));
      if (e.key === "F5") return run(() => rescanLibrary(app));
      if (typing) return;
      if (mod && e.key === "ArrowRight") return run(() => player.next({ fade: player.get().crossfade > 0 }));
      if (mod && e.key === "ArrowLeft") return run(() => player.prev());
      // Screens that edit something save with Ctrl+S; everywhere else it is shuffle.
      if (mod && key === "s" && !SAVES.has(app.nav.view)) return run(() => player.setShuffle(!player.get().shuffle));
      if (e.key === " " && !el?.closest("button, [role='slider'], [role='menu']")) return run(() => player.toggle());
      if (e.key === "?") return run(() => app.openSettings("shortcuts"));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app, layouts]);
}

/** True once the plugins have loaded, or after a short wait if the engine is slow. */
function usePluginsSettled() {
  const [settled, setSettled] = useState(pluginsSettled);
  useEffect(() => {
    if (settled) return;
    const off = bus.on("plugins.ui.synced", () => setSettled(true));
    const timer = window.setTimeout(() => setSettled(true), 2000);
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, [settled]);
  return settled;
}

function Shell() {
  const app = useApp();
  const { layouts } = useRegistry();
  const narrow = useNarrow();
  const settled = usePluginsSettled();
  useShortcuts();
  const found = layouts.find((l) => l.id === app.layout);
  const chosen = found ?? layouts.find((l) => l.id === "pro") ?? layouts[0];
  // A locked layout (Radio's desks, station mode) stays at every width and fits itself.
  const layout = narrow && !app.layoutLocked ? layouts.find((l) => l.id === "compact") ?? chosen : chosen;
  return (
    <>
      {/* Before the layout: the open flow outlives screen and layout changes. */}
      <FlowSession />
      {found || settled || narrow ? layout?.render() : null}
      {app.settingsPage && <SettingsDialog />}
      <EditDialogs />
      <InsertDialog />
      <SetupDialog />
      <FfmpegNotice />
      <Toasts />
      <UpdatePrompt />
    </>
  );
}

export default function App() {
  useEffect(() => checkOnStart(), []);
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
